/* eslint-disable react/jsx-no-constructed-context-values */

/* Shared */
import { LicenseDataStatus } from 'application/Shared/Components/LicenseUsage/types';
import { AccessType } from 'application/Shared/Components/Table/types';
import {
    ClassificationStatus,
    LifecycleStatus,
    RiskLevel,
    TABLENAMES
} from 'application/Shared/Constants';
import { Route, SessionHelper } from 'application/Shared/Helpers';
import { Note, User } from 'application/Shared/Requests';

/* Hooks */
import { useGetDefaultCategoryId } from 'hooks/dataAccess/Category/category';
import {
    useFetchSingleTenantAppAsync,
    usePutTenantApplicationLink
} from 'hooks/dataAccess/Tenant/ApplicationLink/tenantApplicationLink';

/* Hooks */
import { useFetchUserCustomTableViews } from 'hooks/dataAccess/UserCustomTableView/userCustomTableView';
import { useSession } from 'hooks/redux/Selectors/session';
import { useTenantInfoByTenantId } from 'hooks/redux/Selectors/tenants';

/*  React  */
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

const loadNotes = async ({
    aNote,
    setClientNotes,
    setCommunityNotes,
    setPartnerNotes
}) => {
    const clientNotes = await aNote.getClient(
        { excludeCompleted: true, type: '*' },
        { map: true }
    );

    const partnerNotes = await aNote.getPartner(
        { archived: false, excludeCompleted: true, type: '*' },
        { map: true }
    );

    const communityNotes = await aNote.getCommunity(
        {
            approved: true,
            excludeCompleted: true,
            includeImported: true
        },
        { map: true }
    );

    setClientNotes(clientNotes);
    setPartnerNotes(partnerNotes);
    setCommunityNotes(communityNotes);
};

const loadUsers = async ({ aUser, setUserNameMap }) => {
    const users = await aUser.get({ size: -1 });
    if (!users || !Array.isArray(users)) {
        return;
    }

    const userNameMap = users.reduce((acc, user) => {
        acc[user.id] = user.name;
        return acc;
    }, {});

    setUserNameMap(userNameMap);
};

export const ClientAppsTableContext = createContext({});

export const ClientAppsTableContextProvider = ({ children }) => {
    const { t } = useTranslation();
    const { clientId, partnerId } = useParams();
    const session = useSession();
    const sessionHelper = new SessionHelper(session);
    const partnerTenantInfo = useTenantInfoByTenantId(partnerId);
    const partnerTenant = partnerTenantInfo?.tenant;
    // If no partnerId in the URL (such as when client level user is signed in), use session tenant as fallback.
    const tenant = partnerTenant || sessionHelper.getTenant();
    const partner = tenant && tenant.partner;

    const [bulkClassify, setBulkClassify] = useState(false);
    const [classifyId, setClassifyId] = useState(null);
    const [clientNotes, setClientNotes] = useState({});
    const [communityNotes, setCommunityNotes] = useState({});
    const [classifyAppOpen, setClassifyAppOpen] = useState(false);
    const [modalInfo, setModalInfo] = useState(null);
    const [nameMap, setNameMap] = useState({});
    const [partnerNotes, setPartnerNotes] = useState({});
    const [reloadingNotes, setReloadingNotes] = useState(false);
    const [rowMap, setRowMap] = useState({});
    const tableName = TABLENAMES.CLIENTAPPS;
    const [userNameMap, setUserNameMap] = useState({});

    const { putTenantApplicationLink } = usePutTenantApplicationLink();

    const { getTenantApp } = useFetchSingleTenantAppAsync();

    const { defaultCategoryId } = useGetDefaultCategoryId();

    const aNote = useMemo(() => new Note(clientId), [clientId]);
    const aUser = useMemo(() => new User(clientId), [clientId]);

    const route = Route.buildBaseAdminRoute({ clientId, partnerId });

    useEffect(() => {
        async function fetchData() {
            await loadNotes({
                aNote,
                setClientNotes,
                setCommunityNotes,
                setPartnerNotes
            });
        }
        fetchData();
    }, [aNote]);

    useEffect(() => {
        async function fetchData() {
            await loadUsers({
                aUser,
                setUserNameMap
            });
        }
        fetchData();
    }, [aUser]);

    const reloadNotes = useCallback(async () => {
        setReloadingNotes(true);

        await loadNotes({
            aNote,
            setClientNotes,
            setCommunityNotes,
            setPartnerNotes
        });

        setReloadingNotes(false);
    }, [aNote]);

    const columnPinning = {
        left: ['selectAll', 'application'],
        right: ['actions']
    };

    const columnVisibility = {
        actions: true,
        application: true,
        businessOwnerType: true,
        category: false,
        classified: true,
        dateAdded: true,
        lastUsed: true,
        licenseData: true,
        managementStage: false,
        notes: true,
        publisher: false,
        riskFilter: true,
        selectAll: true,
        source: true,
        ssoCoverage: true,
        status: true,
        subCategory: false,
        tags: true,
        usage: false,
        users: true,
        workLoginPercent: true
    };

    const {
        customTableViews,
        loading: loadingCustomTableViews,
        refetchCustomTableViews
    } = useFetchUserCustomTableViews(tableName);

    // Tried to memorize it, but it did not work well with table state changes.
    const tableViews = [
        {
            accessType: AccessType.tenant,
            displayName: t('TableViews.default'),
            id: 'default_presetView',
            state: {
                columnVisibility: { ...columnVisibility, subCategory: true },
                sorting: [{ desc: true, id: 'dateAdded' }]
            }
        },
        {
            accessType: AccessType.tenant,
            displayName: t('TableViews.unclassifiedApps'),
            id: 'unclassifiedApps_presetView',
            state: {
                columnFilters: [
                    {
                        id: 'classified',
                        value: [ClassificationStatus.Unclassified]
                    }
                ]
            }
        },
        {
            accessType: AccessType.tenant,
            displayName: t('TableViews.unapprovedApps'),
            id: 'unapprovedApps_presetView',
            state: {
                columnFilters: [
                    { id: 'status', value: [LifecycleStatus.Unapproved] }
                ],
                sorting: [{ desc: true, id: 'lastUsed' }]
            }
        },
        {
            accessType: AccessType.tenant,
            displayName: t('TableViews.possibleShadowAI'),
            id: 'possibleShadowAI_presetView',
            state: {
                columnFilters: [
                    { id: 'subCategory', value: ['AI'] },
                    {
                        id: 'status',
                        value: [
                            LifecycleStatus.Decommissioned,
                            LifecycleStatus.Decommissioning,
                            LifecycleStatus.NotConfigured,
                            LifecycleStatus.Evaluating,
                            LifecycleStatus.Implementing,
                            LifecycleStatus.Unapproved
                        ]
                    }
                ],
                columnVisibility: { ...columnVisibility, subCategory: true },
                sorting: [{ desc: true, id: 'dateAdded' }]
            }
        },
        {
            accessType: AccessType.tenant,
            displayName: t('TableViews.shadowIT'),
            id: 'shadowIT_presetView',
            state: {
                columnFilters: [
                    {
                        id: 'riskFilter',
                        value: [
                            RiskLevel.Critical,
                            RiskLevel.High,
                            RiskLevel.Medium,
                            RiskLevel.Alert
                        ]
                    },
                    {
                        id: 'classified',
                        value: [ClassificationStatus.Unclassified]
                    }
                ],
                sorting: [{ desc: false, id: 'users' }]
            }
        },
        {
            accessType: AccessType.tenant,
            displayName: t('TableViews.saaSOpsIntegrations'),
            id: 'license_presetView',
            state: {
                columnFilters: [
                    {
                        id: 'licenseData',
                        value: [
                            LicenseDataStatus.available,
                            LicenseDataStatus.notConnected,
                            LicenseDataStatus.syncFailed
                        ]
                    }
                ],
                columnVisibility: {
                    actions: true,
                    application: true,
                    businessOwnerType: true,
                    category: false,
                    classified: false,
                    dateAdded: true,
                    lastUsed: true,
                    licenseData: true,
                    managementStage: false,
                    notes: false,
                    publisher: false,
                    riskFilter: true,
                    selectAll: true,
                    source: true,
                    ssoCoverage: true,
                    status: true,
                    subCategory: false,
                    tags: true,
                    usage: true,
                    users: true,
                    workLoginPercent: false
                },
                sorting: [{ desc: false, id: 'licenseData' }]
            }
        },
        {
            accessType: AccessType.tenant,
            displayName: t('TableViews.highRiskApps'),
            id: 'highRiskApps_presetView',
            state: {
                columnFilters: [
                    {
                        id: 'riskFilter',
                        value: [RiskLevel.Critical, RiskLevel.High]
                    }
                ]
            }
        },
        {
            accessType: AccessType.tenant,
            displayName: t('TableViews.recentlyDiscoveredApps'),
            id: 'recentlyDiscoveredApps_presetView',
            state: {
                columnFilters: [
                    {
                        id: 'dateAdded',
                        value: ['last30days']
                    },
                    {
                        id: 'classified',
                        value: [ClassificationStatus.Unclassified]
                    }
                ]
            }
        }
    ];

    const providerValues = {
        bulkClassify,
        classifyAppOpen,
        classifyId,
        clientId,
        clientNotes,
        columnPinning,
        columnVisibility,
        communityNotes,
        customTableViews,
        defaultCategoryId,
        getTenantApp,
        loadingCustomTableViews,
        modalInfo,
        nameMap,
        partner,
        partnerNotes,
        putTenantApplicationLink,
        refetchCustomTableViews,
        reloadingNotes,
        reloadNotes,
        route,
        rowMap,
        setBulkClassify,
        setClassifyAppOpen,
        setClassifyId,
        setClientNotes,
        setCommunityNotes,
        setModalInfo,
        setNameMap,
        setPartnerNotes,
        setReloadingNotes,
        setRowMap,
        t,
        tableName,
        tableViews,
        userNameMap
    };

    return (
        <ClientAppsTableContext.Provider value={providerValues}>
            {children}
        </ClientAppsTableContext.Provider>
    );
};

export const useClientAppsTableContext = () => {
    const context = useContext(ClientAppsTableContext);

    if (context === undefined) {
        throw new Error(
            '`useClientAppsTableContext` must be used within `ClientAppsTableContextProvider`'
        );
    }

    return context;
};
